'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bell,
  BellOff,
  CheckCheck,
  Loader2,
  ScanLine
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useRouter } from '@/i18n/routing';
import { useRouteAccess } from '@/hooks/use-route-access';
import { useRelativeTime } from '@/hooks/use-relative-time';
import { useNotificationsEnabled } from '@/features/configurations';
import { notificationQueries } from '../services/notifications.queries';
import {
  useMarkAsReadMutation,
  useMarkAllAsReadMutation
} from '../services/notifications.mutations';
import type {
  AppNotification,
  AssignmentCreatedNotification,
  RegistrationRejectedNotification,
  RegistrationRejectionThresholdNotification
} from '../types';

function isAssignmentCreated(
  n: AppNotification
): n is AssignmentCreatedNotification {
  return n.data.type === 'assignment_created';
}

function isRejectionThreshold(
  n: AppNotification
): n is RegistrationRejectionThresholdNotification {
  return n.data.type === 'registration_rejection_threshold_exceeded';
}

const STAGE_STYLES: Record<string, string> = {
  registration: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
};

interface NotificationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationDrawer({
  open,
  onOpenChange
}: NotificationDrawerProps) {
  const t = useTranslations('notifications');
  const tc = useTranslations('common');
  const tg = useTranslations('auth.guard');
  const router = useRouter();
  const canAccess = useRouteAccess();
  const relativeTime = useRelativeTime('notifications');
  const notificationsEnabled = useNotificationsEnabled();

  const { data, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      ...notificationQueries.list({ per_page: 20 }),
      enabled: open && notificationsEnabled
    });

  const notifications = (data?.pages.flatMap((p) => p.data ?? []) ??
    []) as AppNotification[];
  const markAsReadMutation = useMarkAsReadMutation();
  const markAllAsReadMutation = useMarkAllAsReadMutation();

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;

    const root =
      (el.closest('[data-radix-scroll-area-viewport]') as HTMLElement | null) ??
      null;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { root, rootMargin: '120px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, notifications.length]);

  /** The screen a notification points at — one place, so the click and the
   *  permission check can never disagree about where it leads. */
  function targetOf(notification: AppNotification): string {
    if (notification.data.type === 'assignment_created') {
      return '/dashboard/photo-scanning';
    }
    if (
      notification.data.type === 'registration_rejection_threshold_exceeded'
    ) {
      return `/dashboard/containers/${notification.data.registration_id}`;
    }
    return `/dashboard/review/${notification.data.registration_id}`;
  }

  function handleNotificationClick(notification: AppNotification) {
    if (!notification.read_at) {
      markAsReadMutation.mutate(notification.id);
    }

    // A notification is broadcast by stage, not by permission, so an account
    // can legitimately be told about work it cannot open. Say so instead of
    // navigating it into a full-page "Access Denied".
    const target = targetOf(notification);
    if (!canAccess(target)) {
      toast.error(tg('noPermission'));
      return;
    }

    onOpenChange(false);
    router.push(target);
  }

  const stageLabel = (stage: string) => {
    const labels: Record<string, string> = {
      registration: tc('stageRegistration'),
      sticking: tc('stageSticking')
    };
    return labels[stage] ?? stage;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side='right' className='flex w-full flex-col sm:w-[440px]'>
        <SheetHeader className='flex flex-row items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <Bell className='text-primary h-4 w-4' />
            <SheetTitle className='text-base'>{t('title')}</SheetTitle>
          </div>
          {notifications.some((n) => !n.read_at) && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
              className='text-muted-foreground hover:text-foreground h-7 gap-1 px-2 text-xs'
            >
              <CheckCheck className='h-3 w-3' />
              {t('markAllRead')}
            </Button>
          )}
        </SheetHeader>

        <Separator />

        <ScrollArea className='-mx-2 flex-1 overflow-hidden'>
          <div className='px-2'>
            {!notificationsEnabled ? (
              <div className='flex flex-col items-center justify-center py-16 text-center'>
                <div className='bg-muted mb-4 flex h-12 w-12 items-center justify-center rounded-full'>
                  <BellOff className='text-muted-foreground h-6 w-6' />
                </div>
                <p className='text-foreground text-sm font-medium'>
                  {t('notificationsDisabledTitle')}
                </p>
                <p className='text-muted-foreground mt-1 max-w-[240px] text-xs'>
                  {t('notificationsDisabledHint')}
                </p>
              </div>
            ) : isPending ? (
              <div className='divide-border divide-y'>
                {Array.from({ length: 4 }).map((_, i) => (
                  <NotificationSkeleton key={i} />
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-16 text-center'>
                <div className='bg-muted mb-4 flex h-12 w-12 items-center justify-center rounded-full'>
                  <Bell className='text-muted-foreground h-6 w-6' />
                </div>
                <p className='text-foreground text-sm font-medium'>
                  {t('noNotifications')}
                </p>
                <p className='text-muted-foreground mt-1 max-w-[240px] text-xs'>
                  {t('noNotificationsHint')}
                </p>
              </div>
            ) : (
              <div className='divide-border divide-y'>
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    type='button'
                    className={cn(
                      'hover:bg-accent/50 w-full rounded-lg px-3 py-3 text-start transition-colors',
                      !notification.read_at && 'bg-primary/5'
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className='flex items-start gap-3'>
                      {isAssignmentCreated(notification) ? (
                        <AssignmentCreatedRow
                          notification={notification}
                          relativeTime={relativeTime}
                          t={t}
                        />
                      ) : isRejectionThreshold(notification) ? (
                        <RejectionThresholdRow
                          notification={notification}
                          relativeTime={relativeTime}
                          t={t}
                        />
                      ) : (
                        <RegistrationRejectedRow
                          notification={notification}
                          relativeTime={relativeTime}
                          t={t}
                          stageLabel={stageLabel}
                        />
                      )}
                    </div>
                  </button>
                ))}

                {(hasNextPage || isFetchingNextPage) && (
                  <div
                    ref={sentinelRef}
                    className='text-muted-foreground flex items-center justify-center gap-2 py-4 text-xs'
                  >
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    {t('loadingMore')}
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

interface RowProps<TNotification extends AppNotification> {
  notification: TNotification;
  relativeTime: (date: string) => string;
  t: ReturnType<typeof useTranslations<'notifications'>>;
}

function RegistrationRejectedRow({
  notification,
  relativeTime,
  t,
  stageLabel
}: RowProps<RegistrationRejectedNotification> & {
  stageLabel: (stage: string) => string;
}) {
  return (
    <>
      <div className='relative shrink-0'>
        <div className='flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-sm font-semibold text-red-700 dark:bg-red-900 dark:text-red-300'>
          {notification.data.reviewer_name?.charAt(0).toUpperCase() ?? '?'}
        </div>
        {!notification.read_at && (
          <span className='bg-destructive ring-background absolute -end-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full ring-2' />
        )}
      </div>

      <div className='min-w-0 flex-1'>
        <div className='flex items-center justify-between gap-2'>
          <p className='truncate text-sm font-medium'>
            {notification.data.reviewer_name}
          </p>
          <span className='text-muted-foreground shrink-0 text-[11px] tabular-nums'>
            {relativeTime(notification.created_at)}
          </span>
        </div>

        <p className='text-muted-foreground mt-0.5 text-xs'>
          {t('containerRejected', {
            number: notification.data.container_number
          })}
        </p>

        <div className='mt-1.5 flex flex-wrap items-center gap-1.5'>
          <Badge
            variant='secondary'
            className={cn(
              'h-5 px-1.5 text-[11px] font-medium',
              STAGE_STYLES[notification.data.target_stage] ?? ''
            )}
          >
            {t('rejectedTo')} {stageLabel(notification.data.target_stage)}
          </Badge>
        </div>

        <p className='text-muted-foreground mt-1 line-clamp-2 text-[11px]'>
          {notification.data.rejection_reason}
        </p>
      </div>
    </>
  );
}

function AssignmentCreatedRow({
  notification,
  relativeTime,
  t
}: RowProps<AssignmentCreatedNotification>) {
  const { assignment_count, assignment_numbers, device_name, created_by_name } =
    notification.data;
  const previewNumbers = assignment_numbers.slice(0, 2).join(', ');
  const extraCount = assignment_numbers.length - 2;

  return (
    <>
      <div className='relative shrink-0'>
        <div className='flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'>
          <ScanLine className='h-4 w-4' />
        </div>
        {!notification.read_at && (
          <span className='bg-primary ring-background absolute -end-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full ring-2' />
        )}
      </div>

      <div className='min-w-0 flex-1'>
        <div className='flex items-center justify-between gap-2'>
          <p className='truncate text-sm font-medium'>
            {t('assignmentCreatedTitle', { count: assignment_count })}
          </p>
          <span className='text-muted-foreground shrink-0 text-[11px] tabular-nums'>
            {relativeTime(notification.created_at)}
          </span>
        </div>

        <p className='text-muted-foreground mt-0.5 text-xs'>
          {t('assignmentCreatedBody', {
            device: device_name,
            assignedBy: created_by_name ?? t('aSupervisor')
          })}
        </p>

        <p className='text-muted-foreground mt-1 line-clamp-1 text-[11px]'>
          {previewNumbers}
          {extraCount > 0 ? ' ' + t('andMore', { count: extraCount }) : ''}
        </p>
      </div>
    </>
  );
}

function RejectionThresholdRow({
  notification,
  relativeTime,
  t
}: RowProps<RegistrationRejectionThresholdNotification>) {
  const { container_number, rejection_count, threshold, last_reason } =
    notification.data;

  return (
    <>
      <div className='relative shrink-0'>
        <div className='flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'>
          <AlertTriangle className='h-4 w-4' />
        </div>
        {!notification.read_at && (
          <span className='bg-destructive ring-background absolute -end-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full ring-2' />
        )}
      </div>

      <div className='min-w-0 flex-1'>
        <div className='flex items-center justify-between gap-2'>
          <p className='truncate text-sm font-medium'>
            {t('rejectionThresholdTitle')}
          </p>
          <span className='text-muted-foreground shrink-0 text-[11px] tabular-nums'>
            {relativeTime(notification.created_at)}
          </span>
        </div>

        <p className='text-muted-foreground mt-0.5 text-xs'>
          {t('rejectionThresholdBody', {
            container: container_number,
            count: rejection_count,
            threshold
          })}
        </p>

        {last_reason ? (
          <p className='text-muted-foreground mt-1 line-clamp-2 text-[11px]'>
            {last_reason}
          </p>
        ) : null}
      </div>
    </>
  );
}

function NotificationSkeleton() {
  return (
    <div className='flex items-start gap-3 px-1 py-3'>
      <div className='bg-muted h-9 w-9 shrink-0 animate-pulse rounded-full' />
      <div className='flex-1 space-y-2'>
        <div className='flex items-center justify-between'>
          <div className='bg-muted h-3.5 w-24 animate-pulse rounded' />
          <div className='bg-muted h-3 w-14 animate-pulse rounded' />
        </div>
        <div className='bg-muted h-3 w-40 animate-pulse rounded' />
        <div className='bg-muted h-5 w-28 animate-pulse rounded-md' />
      </div>
    </div>
  );
}
